import type { UseFormSetValue } from 'react-hook-form';

import { isoDateValue } from './trade-invoice-payload';
import { usesSimpleLayout } from './adjustment-note-schema';
import type { AdjustmentLineValues, AdjustmentNoteSubmitValues } from './adjustment-note-schema';
import type { InvoiceRow } from '../types';

function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`);
}

function todayIso(): string {
  return isoDateValue(new Date());
}

function fallbackNumber(raw: string | null, fallback: number): number {
  return raw === null ? fallback : Number(raw);
}

function fallbackDate(raw: string | null, fallback: Date): Date {
  return raw === null ? fallback : parseIsoDate(raw.slice(0, 10));
}

// Falls back to USD when the parent carries a currency the dialog can't
// represent (the schema only enumerates USD/EUR; the backend accepts the
// full enum). Throwing here used to crash the edit dialog on legacy rows
// that carried GBP/CHF/etc. — the operator can still pick the right
// currency manually before saving.
function inheritedCurrency(parent: InvoiceRow | null): 'USD' | 'EUR' {
  if (parent === null) return 'USD';
  if (parent.currency === 'USD' || parent.currency === 'EUR') return parent.currency;
  return 'USD';
}

const ALLOWED_UNITS: readonly string[] = ['days', 'mt', 'each', 'other'];

function normalisedUnit(raw: unknown): AdjustmentLineValues['unit'] {
  const unit = typeof raw === 'string' ? raw : 'days';
  return (ALLOWED_UNITS.includes(unit) ? unit : 'days') as AdjustmentLineValues['unit'];
}

function strOrUndefined(raw: unknown): string | undefined {
  return typeof raw === 'string' ? raw : undefined;
}

function lineFromUnknown(raw: unknown): AdjustmentLineValues | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const type = strOrUndefined(r['type']);
  return {
    ...(type !== undefined ? { type } : {}),
    description: strOrUndefined(r['description']) ?? '',
    quantity: Number(r['quantity'] ?? 0),
    unit: normalisedUnit(r['unit']),
    unit_price: Number(r['unit_price'] ?? 0),
    line_total: Number(r['line_total'] ?? 0),
  };
}

function linesFromExisting(existing: InvoiceRow | null): AdjustmentLineValues[] | undefined {
  if (existing === null) return undefined;
  const raw = existing.adjustment_lines;
  if (!Array.isArray(raw)) return undefined;
  const parsed = raw.map(lineFromUnknown).filter((x): x is AdjustmentLineValues => x !== null);
  return parsed.length === 0 ? undefined : parsed;
}

export interface BuildAdjustmentNoteDefaultsArgs {
  readonly noteType: 'Credit' | 'Debit';
  readonly side: 'buy' | 'sell';
  readonly parent: InvoiceRow | null;
  readonly existing: InvoiceRow | null;
  readonly blOrderName?: string | null;
}

function isSellNote(args: BuildAdjustmentNoteDefaultsArgs): boolean {
  return args.side === 'sell';
}

// The sell-side note hides its number behind an auto-value, so an empty
// default would silently block submit. This deterministic per-BL string only
// covers the window before the chain-aware server suggestion (CN-2026-01700-03)
// resolves — the prefill hook upgrades an untouched field holding exactly this
// value to the suggestion.
export function sellNoteInvoiceNumber(
  noteType: 'Credit' | 'Debit',
  blOrderName: string | null | undefined,
): string {
  const prefix = noteType === 'Credit' ? 'CREDIT' : 'DEBIT';
  const name = blOrderName?.trim();
  return `${prefix}-${name !== undefined && name !== '' ? name : 'INVOICE'}`;
}

export function emptySellLine(): AdjustmentLineValues {
  return { type: '', description: '', quantity: 1, unit: 'each', unit_price: 0, line_total: 0 };
}

function initialParentId(args: BuildAdjustmentNoteDefaultsArgs): string {
  return args.existing?.adjusts_invoice_id ?? args.parent?.id ?? '';
}

function initialCompanyName(args: BuildAdjustmentNoteDefaultsArgs): string | null {
  return args.existing?.company_name ?? args.parent?.company_name ?? null;
}

function initialIssuerName(args: BuildAdjustmentNoteDefaultsArgs): string | null {
  return args.existing?.issuer_company_name ?? args.parent?.issuer_company_name ?? null;
}

export function buildAdjustmentNoteDefaults(
  args: BuildAdjustmentNoteDefaultsArgs,
): AdjustmentNoteSubmitValues {
  const base = requiredDefaults(args);
  applyOptionalDefaults(base, args);
  applySellNoteLines(base, args);
  applySettlementDefaults(base, args);
  applySimpleLayoutDefaults(base, args);
  applySellNoteDefaults(base, args);
  return base;
}

// The sell-side notes itemise the charge and auto-derive their identity fields
// (Launchpad parity): the issue date is today and the invoice number is the
// deterministic per-BL number, neither of which the operator edits.
function applySellNoteDefaults(
  base: AdjustmentNoteSubmitValues,
  args: BuildAdjustmentNoteDefaultsArgs,
): void {
  if (!isSellNote(args)) return;
  const issued = args.existing?.issue_date ?? null;
  base.issue_date = issued !== null ? issued.slice(0, 10) : todayIso();
  if (args.existing === null && base.invoice_number === '') {
    base.invoice_number = sellNoteInvoiceNumber(args.noteType, args.blOrderName);
  }
}

// New sell-side notes start with one empty line so the operator sees the line
// form immediately and the total stays at zero until it's filled. Editing pulls
// the persisted lines back.
function applySellNoteLines(
  base: AdjustmentNoteSubmitValues,
  args: BuildAdjustmentNoteDefaultsArgs,
): void {
  if (!isSellNote(args)) return;
  const lines = linesFromExisting(args.existing);
  if (lines !== undefined) {
    base.adjustment_lines = lines;
    return;
  }
  if (args.existing === null) base.adjustment_lines = [emptySellLine()];
}

// The simple supplier-invoice layout (buy side) carries the presentational
// Status select (defaults to Draft) and pre-fills the supplier invoice date
// with today (Launchpad parity). The native date input needs a YYYY-MM-DD
// string to render a value; zod coerces it back to a Date on submit, so the
// default is held as a string here.
function applySimpleLayoutDefaults(
  base: AdjustmentNoteSubmitValues,
  args: BuildAdjustmentNoteDefaultsArgs,
): void {
  if (!usesSimpleLayout(args.noteType, args.side)) return;
  base.status = 'Draft';
  const issued = args.existing?.issue_date ?? null;
  base.issue_date = issued !== null ? issued.slice(0, 10) : todayIso();
}

// New sell-side Debit notes default a settlement method + payment terms
// (Launchpad parity). The simple layout (buy side) has no settlement field.
// Editing an existing note leaves these to the operator (the row does not carry
// them back on the wire).
function applySettlementDefaults(
  base: AdjustmentNoteSubmitValues,
  args: BuildAdjustmentNoteDefaultsArgs,
): void {
  if (args.existing !== null) return;
  if (args.noteType === 'Debit' && !usesSimpleLayout(args.noteType, args.side)) {
    base.settlement_method = 'Payable by TT';
    base.payment_terms = 'Due immediately';
  }
}

function requiredDefaults(args: BuildAdjustmentNoteDefaultsArgs): AdjustmentNoteSubmitValues {
  return {
    adjusts_invoice_id: initialParentId(args),
    invoice_number: args.existing?.invoice_number ?? '',
    currency: inheritedCurrency(args.existing ?? args.parent),
    total_amount: fallbackNumber(args.existing?.total_amount ?? null, 0),
    vat_amount: fallbackNumber(args.existing?.vat_amount ?? null, 0),
    original_due_date: fallbackDate(
      args.existing?.original_due_date ?? null,
      parseIsoDate(todayIso()),
    ),
  };
}

function applyOptionalDefaults(
  base: AdjustmentNoteSubmitValues,
  args: BuildAdjustmentNoteDefaultsArgs,
): void {
  const reason = args.existing?.note_reason ?? null;
  if (reason !== null) base.note_reason = reason;
  const company = initialCompanyName(args);
  if (company !== null) base.company_name = company;
  const issuer = initialIssuerName(args);
  if (args.side === 'buy' && issuer !== null) base.issuer_company_name = issuer;
  const remarks = args.existing?.remarks ?? null;
  if (remarks !== null) base.remarks = remarks;
}

// The note fields a parent hands down (currency, counterparty, buy-side
// issuer). Shared by the picker's manual selection and the follow-on path's
// automatic parent seed so both routes inherit identically.
export function applyParentInheritance(
  picked: InvoiceRow | null,
  side: 'buy' | 'sell',
  setValue: UseFormSetValue<AdjustmentNoteSubmitValues>,
): void {
  if (picked === null) return;
  if (picked.currency === 'EUR' || picked.currency === 'USD') {
    setValue('currency', picked.currency, { shouldDirty: true });
  }
  if (picked.company_name !== null) {
    setValue('company_name', picked.company_name, { shouldDirty: true });
  }
  if (side === 'buy' && picked.issuer_company_name !== null) {
    setValue('issuer_company_name', picked.issuer_company_name, { shouldDirty: true });
  }
}

export interface AdjustmentPrefill {
  readonly values: Partial<AdjustmentNoteSubmitValues>;
  readonly aiExtractionId: string;
}

export function applyAdjustmentPrefill(
  defaults: AdjustmentNoteSubmitValues,
  prefill: AdjustmentPrefill | undefined,
): AdjustmentNoteSubmitValues {
  if (prefill === undefined) return defaults;
  return { ...defaults, ...prefill.values };
}
